import { z } from 'zod';
import { isSupportedCurrency } from '@fint/money';
/** A postal address. Stored as JSON on customers, vendors and documents. */
export const addressSchema = z
    .object({
    attention: z.string().trim().max(120).optional(),
    line1: z.string().trim().max(200).optional(),
    line2: z.string().trim().max(200).optional(),
    city: z.string().trim().max(100).optional(),
    /** Free text. The GST decision uses `stateCode`, never this. */
    state: z.string().trim().max(100).optional(),
    /** Two-letter state code, for the GST place-of-supply comparison. */
    stateCode: z.string().trim().toUpperCase().max(4).optional(),
    postalCode: z.string().trim().max(20).optional(),
    country: z.string().trim().max(100).optional(),
    phone: z.string().trim().max(40).optional(),
})
    .strict();
export const EMPTY_ADDRESS = {};
/** True when nothing has been filled in - used to decide whether to print it. */
export function isEmptyAddress(a) {
    if (!a)
        return true;
    return !Object.values(a).some((v) => typeof v === 'string' && v.trim().length > 0);
}
/**
 * An amount arriving from a client.
 *
 * Accepts a string or a number and keeps it as a STRING, because that is what
 * @fint/money parses and what the DECIMAL column stores. Coercing to a number
 * here would put every amount through a float on its way in, which is the one
 * thing the money package exists to prevent.
 */
export const amountSchema = z
    .union([z.string().trim(), z.number()])
    .transform((v) => (typeof v === 'number' ? String(v) : v))
    .refine((v) => v === '' || /^[+-]?(\d+(\.\d*)?|\.\d+)$/.test(v.replace(/[\s,]/g, '')), {
    message: 'Enter a valid amount',
});
/** A quantity or unit rate: same reasoning, more decimal places. */
export const quantitySchema = z
    .union([z.string().trim(), z.number()])
    .transform((v) => (typeof v === 'number' ? v : Number(v.replace(/[\s,]/g, ''))))
    .refine((v) => Number.isFinite(v), { message: 'Enter a valid quantity' });
/**
 * A calendar date with no time component, as `YYYY-MM-DD`.
 *
 * Invoice dates are dates, not instants. Storing one as a timestamp means an
 * invoice dated the 1st in Chennai is dated the 31st in London, and the due
 * date it drives moves with it. So these never become `Date` on the way in.
 */
export const dateOnlySchema = z
    .string()
    .trim()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD')
    .refine((v) => !Number.isNaN(Date.parse(`${v}T00:00:00Z`)), { message: 'Not a real date' });
export const paginationSchema = z
    .object({
    cursor: z.string().trim().max(200).optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
})
    .strict();
/** A discount, as entered on a line or a document. */
export const discountSchema = z
    .object({
    type: z.enum(['percentage', 'amount']),
    value: z.number().min(0),
})
    .strict()
    .refine((d) => d.type !== 'percentage' || d.value <= 100, {
    message: 'A percentage discount cannot exceed 100%',
    path: ['value'],
});
// ── Currency ──────────────────────────────────────────────────────────────
/**
 * A currency code, and only one the money package knows.
 *
 * `length(3)` used to be the whole check, so "XYZ" was accepted here and died
 * later inside `parse()` as a 500. A currency Fint has no exponent for cannot
 * be rounded, so it is refused at the door with the list it is not on.
 */
export const currencyCodeSchema = z
    .string()
    .trim()
    .toUpperCase()
    .length(3, 'A currency is a three-letter code like INR or GBP')
    .refine((code) => isSupportedCurrency(code), { message: 'Fint does not know that currency' });
/**
 * An exchange rate: base-currency units per ONE unit of the foreign
 * currency (rupees per pound, 127.5000). A string or a number, kept as a
 * NUMBER - unlike an amount, a rate is a multiplier, and eight places is
 * what the column stores.
 */
export const exchangeRateSchema = z
    .union([z.string().trim(), z.number()])
    .transform((v) => (typeof v === 'number' ? v : Number(v.replace(/[\s,]/g, ''))))
    .refine((v) => Number.isFinite(v) && v > 0, { message: 'Enter the rate as a positive number, e.g. 127.5' })
    .refine((v) => v < 1_000_000, { message: 'That rate is not plausible' })
    .transform((v) => Number(v.toFixed(8)));
/**
 * Where a rate came from. `manual` a person typed it; `fbil` / `ecb` a
 * published reference rate; `last` the rate on the customer's previous
 * document; `inherited` copied from the document a credit note reverses;
 * `bank` derived from what the account was credited.
 */
export const EXCHANGE_RATE_SOURCES = ['manual', 'fbil', 'ecb', 'last', 'inherited', 'bank'];
export const exchangeRateSourceSchema = z.enum(EXCHANGE_RATE_SOURCES);
//# sourceMappingURL=common.js.map